// base
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
// contexts
import { useNotification } from 'contexts'; // modules
import {
  BeaconAtomFamily,
  BeaconCreateModeAtom,
  BeaconGetInfoDataAtom,
  CanvasTypeAtom,
  CurrentSiteInformationAtom,
  DrawingUnitsAtom,
  FileChangedGroupIdsAtom,
  LoadingAtom,
  MarkerAtomFamily,
  MarkerCreateModeAtom,
  ModelBuildingInfoListAtom,
  ParentDataAtom,
  ParentDataSettingAtom,
  ParentMetaDataAtom,
  ParentModeAtom,
  PlayerDataAtom,
  PoiAtomFamily,
  RiskAssessmentDataAtom,
  RiskAssessmentDateDataAtom,
  RiskAssessmentGetInfoDataAtom,
  RiskAssessmentListAtom,
  SelectedRiskAssessmentIdAtom,
  TransferPhotoDeleteRequestAtom,
  TransferPhotoFileKeyAtom,
  TransferPhotoListAtom,
  UserInfoAtom,
  ViewerSiderActiveTabKeyAtom
} from 'modules'; // libs
import { formatBuildingData, formatRoomInfoPaju } from 'libs'; // variables
import { ALL_HIDDEN, MAIN_OBJECT, poiModels, siteInformation } from 'variables'; // libraries
import _ from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { GPSWorkerAtomFamily } from 'modules/gps/index.js';
import { createLatLngToModelTransformer } from 'libs/coordinates.js';
import { usePhotoSave } from './usePhotoSave.js';

export const useParentData = () => {
  const iframeLoadedRef = useRef(false);

  const { savePhotos } = usePhotoSave();

  const [parentData, setParentData] = useRecoilState(ParentDataAtom);
  const [parentMetaData, setParentMetaData] = useRecoilState(ParentMetaDataAtom);
  const [parentMode, setParentMode] = useRecoilState(ParentModeAtom);

  const riskAssessmentList = useRecoilValue(RiskAssessmentListAtom);

  const setPlayerData = useSetRecoilState(PlayerDataAtom);
  const setUserInfo = useSetRecoilState(UserInfoAtom);
  const setCanvasType = useSetRecoilState(CanvasTypeAtom);
  const setTimelineDate = useSetRecoilState(RiskAssessmentDateDataAtom);
  const setMarkerCreateMode = useSetRecoilState(MarkerCreateModeAtom);
  const setBeaconCreateMode = useSetRecoilState(BeaconCreateModeAtom);
  const setParentDataSetting = useSetRecoilState(ParentDataSettingAtom);
  const setSiteInformation = useSetRecoilState(CurrentSiteInformationAtom);
  const setSelectedRiskAssessment = useSetRecoilState(SelectedRiskAssessmentIdAtom);
  const setActiveTabKey = useSetRecoilState(ViewerSiderActiveTabKeyAtom);
  const setDrawingUnits = useSetRecoilState(DrawingUnitsAtom);
  const setLoading = useSetRecoilState(LoadingAtom);
  const setFileChangedGroupIds = useSetRecoilState(FileChangedGroupIdsAtom);

  const location = useLocation();
  const type = new URLSearchParams(location.search).get('type');

  const { openNotification } = useNotification();

  const whiteListRef = useRef(import.meta.env.VITE_APP_WHITE_LIST.split(','));

  const setMarkerAtomFamily = useRecoilCallback(
    ({ set }) =>
      (markers) => {
        set(MarkerAtomFamily('all'), markers);
      },
    []
  );

  const setPoiAtomFamily = useRecoilCallback(
    ({ set }) =>
      (pois) => {
        set(PoiAtomFamily('all'), pois);
      },
    []
  );

  const setBeaconAtomFamily = useRecoilCallback(
    ({ set }) =>
      (pois) => {
        set(BeaconAtomFamily('all'), pois);
      },
    []
  );

  const setGPSWorkerAtomFamily = useRecoilCallback(
    ({ set }) =>
      (pois) => {
        set(GPSWorkerAtomFamily('all'), pois);
      },
    []
  );

  /** 부모로 받은 데이터 상태 초기화 */
  const resetRecoilState = useCallback(() => {
    setParentData(undefined);
    setParentMetaData(undefined);
    setParentMode(undefined);
    setParentDataSetting(false);
    setMarkerCreateMode(false);
    setBeaconCreateMode(false);
    setCanvasType('default');
    setLoading(true);
    setFileChangedGroupIds([]);
  }, [
    setParentData,
    setParentMetaData,
    setMarkerCreateMode,
    setBeaconCreateMode,
    setParentMode,
    setCanvasType,
    setParentDataSetting,
    setLoading,
    setFileChangedGroupIds
  ]);

  /** iframe이 열린뒤 부모에게 데이터 보내기 */
  const notifyParent = useCallback(() => {
    const result = {
      pageId: null,
      messageType: 'onLoadPage'
    };

    if (!iframeLoadedRef.current) {
      iframeLoadedRef.current = true;

      console.log('=== notifyParent iframe이 열렸습니다. === : ', result);

      window.parent.postMessage(result, '*');
    }
  }, []);

  const handleRiskAssessmentGetInfoType = useRecoilCallback(
    ({ snapshot }) =>
      async (origin) => {
        setLoading(true);
        const riskAssessmentData = await snapshot.getPromise(RiskAssessmentDataAtom);
        const transferPhotoList = await snapshot.getPromise(TransferPhotoListAtom);
        const transferPhotoDeleteRequest = await snapshot.getPromise(
          TransferPhotoDeleteRequestAtom
        );
        const transferPhotoFileKey = await snapshot.getPromise(TransferPhotoFileKeyAtom);

        const hadChanges =
          transferPhotoList.some((p) => p.source === 'capture') ||
          (transferPhotoDeleteRequest.length > 0 && !!transferPhotoFileKey);

        // 파일 업로드/삭제는 savePhotos()로 위임
        let uploadedFileKey = transferPhotoFileKey;
        try {
          if (hadChanges) {
            uploadedFileKey = await savePhotos();
          }
        } catch (error) {
          console.error('=== 파일 업로드/삭제 실패 ===', error);
        }

        const postSuccessData = await snapshot.getPromise(RiskAssessmentGetInfoDataAtom);
        const currentOriginId = riskAssessmentData?.originId;

        const payload =
          uploadedFileKey || hadChanges
            ? {
                ...postSuccessData,
                markerGroups: postSuccessData.markerGroups.map((markerGroup) =>
                  Number(markerGroup.id) === Number(currentOriginId)
                    ? {
                        ...markerGroup,
                        ...(uploadedFileKey && { fileKey: uploadedFileKey }),
                        ...(hadChanges && { isFileChanged: true })
                      }
                    : markerGroup
                )
              }
            : postSuccessData;

        console.log('=== handleRiskAssessmentGetInfoType postSuccessData === : ', payload);

        window.parent.postMessage({ ...payload, messageType: 'getInfo' }, origin);
        resetRecoilState();
      },
    [resetRecoilState, savePhotos]
  );

  const handleBeaconGetInfoType = useRecoilCallback(
    ({ snapshot }) =>
      async (origin) => {
        const postSuccessData = await snapshot.getPromise(BeaconGetInfoDataAtom);

        console.log('=== handleBeaconGetInfoType postSuccessData === : ', postSuccessData);

        window.parent.postMessage({ ...postSuccessData, messageType: 'getInfo' }, origin);
        resetRecoilState();
      },
    [resetRecoilState]
  );

  const handleGetProcessRate = useRecoilCallback(
    ({ snapshot, set }) =>
      async (data) => {
        if (!data) return;

        const buildingInfoList = await snapshot.getPromise(ModelBuildingInfoListAtom);
        const currentSiteInfo = await snapshot.getPromise(CurrentSiteInformationAtom);

        if (!buildingInfoList) return;

        const { modeling } = data;
        if (!modeling) return;

        const { buildings, maxBasement } = modeling;

        // 둘 다 없으면 처리하지 않음
        if (!buildings && !maxBasement) return;

        const isSpecialSite = currentSiteInfo === 'S001' || currentSiteInfo === 'S002';

        /**
         * ---------------------------
         * 1️⃣ maxBasement-only mode
         * ---------------------------
         */
        if (maxBasement && (!buildings || buildings.length === 0)) {
          const res = buildingInfoList.map((building) => {
            const { buildingCode } = building;

            if (isSpecialSite) {
              // 특수 사이트: D0000만 표시
              if (buildingCode === 'D0000') {
                return {
                  ...building,
                  currentFloor: maxBasement
                };
              }

              return {
                ...building,
                currentFloor: ALL_HIDDEN
              };
            }

            // 일반 사이트
            if (buildingCode === 'Basement') {
              return {
                ...building,
                currentFloor: maxBasement
              };
            }

            if (buildingCode === 'Ground') {
              return {
                ...building,
                currentFloor: ALL_HIDDEN
              };
            }

            return {
              ...building,
              currentFloor: ALL_HIDDEN
            };
          });

          set(ModelBuildingInfoListAtom, res);
          return;
        }

        /**
         * ---------------------------
         * 2️⃣ buildings 존재
         * ---------------------------
         */
        if (buildings && buildings.length > 0) {
          const formattedBuildings = formatBuildingData(buildings, currentSiteInfo);

          const res = buildingInfoList.map((building) => {
            const { buildingCode } = building;

            // 특수 사이트 처리
            if (isSpecialSite) {
              if (buildingCode === 'D0000') {
                return {
                  ...building,
                  currentFloor: MAIN_OBJECT
                };
              }

              const matchedBuilding = formattedBuildings.find((item) => item.code === buildingCode);

              if (matchedBuilding?.currentFloor) {
                return {
                  ...building,
                  currentFloor: matchedBuilding.currentFloor
                };
              }

              return {
                ...building,
                currentFloor: ALL_HIDDEN
              };
            }

            // 일반 사이트 처리
            if (buildingCode === 'Basement' || buildingCode === 'Ground') {
              return {
                ...building,
                currentFloor: MAIN_OBJECT
              };
            }

            const matchedBuilding = formattedBuildings.find((item) => item.code === buildingCode);

            if (matchedBuilding?.currentFloor) {
              return {
                ...building,
                currentFloor: matchedBuilding.currentFloor
              };
            }

            return building;
          });

          set(ModelBuildingInfoListAtom, res);
        }
      },
    []
  );

  /** 부모로 부터 받은 데이터 검증 및 커스텀 */
  const handleParentMessage = useCallback(
    async (e) => {
      const origin = e.origin || e.originalEvent.origin;

      if (typeof e.data !== 'object' || !e.data.pageId) return;

      /** 메시지 발신자 검증 (보안을 위해 중요) */
      // if (!whiteListRef.current.includes(origin)) {
      //   console.log(whiteListRef.current);
      //   console.log(origin);
      //
      //   openNotification({
      //     description: '허용되지 않은 출처로부터 메시지를 받았습니다.',
      //     type: 'error'
      //   });
      //   return;
      // }

      try {
        const data = e.data;

        console.log('=== CHILD 부모로 부터 받은 데이터 === : ', e);

        if (data) {
          const { pageId, messageType, siteCode, viewerType } = data;

          if (!['initPage', 'getInfo', 'initProcessRate'].includes(messageType)) {
            openNotification({
              description: '잘못된 메시지 타입을 받았습니다.'
            });

            const failMessageTypeData = {
              pageId,
              messageType
            };

            window.parent.postMessage(failMessageTypeData, origin);

            return;
          }

          /** initPage */
          if (messageType === 'initPage') {
            const cloudLabUser = {
              username: 'cloudlab',
              role: 'PARTNER'
            };

            setUserInfo(cloudLabUser);

            let newData = { ...data };

            if (data.markerGroups && data.markerGroups.length > 0) {
              newData = {
                ...newData,
                markerGroups: newData.markerGroups.map((item) => {
                  const newId = item.id || uuid();

                  return {
                    ...item,
                    id: newId,
                    markers:
                      item.markers && item.markers.length > 0
                        ? item.markers.map((marker) => ({
                            ...marker,
                            originId: newId,
                            fileKey: item.fileKey
                          }))
                        : [],
                    pois:
                      item.pois && item.pois.length > 0
                        ? item.pois.map((poi) => ({
                            ...poi,
                            originId: newId,
                            fileKey: item.fileKey
                          }))
                        : []
                  };
                })
              };

              const allMarkers = _.chain(newData.markerGroups)
                .filter((item) => item.markers)
                .map((item) => {
                  return item.markers.map((marker) => {
                    const [objectName, selectedFloor] = marker.key.split('-');

                    return {
                      ...marker,
                      id: uuid(),
                      title: marker.label,
                      data: {
                        ...newData,
                        originId: marker.originId,
                        fileKey: item.fileKey ?? null
                      },
                      objectName,
                      parentId: item.id,
                      selectedFloor,
                      thumbnail: newData.company?.ciUrl,
                      position: [Number(marker.x), Number(marker.y), Number(marker.z)],
                      type: 'marker',
                      structureOptions:
                        marker?.type || (objectName && formatRoomInfoPaju(objectName)) || {}
                    };
                  });
                })
                .value()
                .flatMap((item) => item);

              const allPois = _.chain(newData.markerGroups)
                .filter((item) => item.pois)
                .map((item) => {
                  return item.pois.map((poi) => {
                    const findModel = poiModels.find((model) => model.key === poi.key);

                    return {
                      ...poi,
                      id: uuid(),
                      title: poi.label,
                      data: {
                        ...newData,
                        originId: poi.originId,
                        fileKey: item.fileKey ?? null
                      },
                      objectName: poi.key,
                      originId: item.id,
                      parentId: item.id,
                      selectedFloor: poi.type.floorCode,
                      selectedBuilding: poi.type.buildingCode,
                      thumbnail: newData.company?.ciUrl,
                      position: [Number(poi.x), Number(poi.y), Number(poi.z)],
                      type: 'poi',
                      poiData: {
                        ...findModel,
                        poiScale: poi.scale || [1, 1, 1],
                        poiRotation: poi.rotation || [1, 1, 1]
                      },
                      structureOptions: poi?.type || {}
                    };
                  });
                })
                .value()
                .flatMap((item) => item);

              const isFocusedRiskData = newData.markerGroups.find((item) => item.isFocused);

              if (isFocusedRiskData) {
                setSelectedRiskAssessment(isFocusedRiskData.id);
              }

              setPoiAtomFamily([...allPois]);
              setMarkerAtomFamily([...allMarkers]);
            }

            if (newData.beaconGroups && newData.beaconGroups.length > 0) {
              const allBeacons = _.chain(newData.beaconGroups)
                .map((beaconGroup) => {
                  const beacon = beaconGroup.beacon;
                  const workers = beaconGroup.workers ?? [];

                  return {
                    ...beaconGroup,
                    ...(beacon ?? {}),
                    workers,
                    id: beaconGroup.id ?? uuid(),
                    position: beacon
                      ? [Number(beacon.x), Number(beacon.y), Number(beacon.z)]
                      : null,
                    type: 'beacon',
                    structureOptions: beacon?.type || {}
                  };
                })
                .value();

              setBeaconAtomFamily([...allBeacons]);
            }

            if (siteCode) {
              const splitSiteCode = siteCode.split('_');
              const originSiteCode = splitSiteCode[splitSiteCode.length - 1];

              setSiteInformation(originSiteCode);

              const isCanvasType =
                originSiteCode === 'S001' ? 'bundang' : 'S002' ? 'paju' : 'default';

              const findSiteInfo = siteInformation.find((item) => item.siteType === originSiteCode);

              if (findSiteInfo) {
                setDrawingUnits(findSiteInfo.drawingUnits);
              }

              setCanvasType(isCanvasType);

              const referencePoints = siteInformation.find(
                (item) => item.siteType === originSiteCode
              )?.referencePoints;

              if (
                referencePoints &&
                referencePoints.length > 0 &&
                newData.gpsGroups &&
                newData.gpsGroups.length > 0
              ) {
                const { latLngToModelPosition } = createLatLngToModelTransformer(referencePoints);

                const allGps = _.chain(newData.gpsGroups)
                  .map((gpsGroup) => {
                    const gps = gpsGroup.gps;
                    const { x, z } = latLngToModelPosition(gps.lat, gps.lng);

                    return {
                      ...gpsGroup,
                      ...(gps ?? {}),
                      position: gps ? [x, 0, z] : null,
                      id: uuid(),
                      type: 'gps'
                    };
                  })
                  .value();

                setGPSWorkerAtomFamily([...allGps]);
              }
            }

            if (viewerType) {
              const lowerCaseViewerType = viewerType.toLocaleLowerCase();
              setParentMode(lowerCaseViewerType);

              if (lowerCaseViewerType === 'register') {
                setMarkerCreateMode(true);
                setActiveTabKey('risk-assessment');
              } else if (lowerCaseViewerType === 'beacon_register') {
                setActiveTabKey('beacon-list');
              }
            }

            if (data.timeline) {
              setTimelineDate(data.timeline);
            }

            setParentDataSetting(true);
            setParentData(newData);
            setParentMetaData({ pageId, messageType, origin, timeline: data.timeline });

            /**
             * 여기에서 데이터를 사용하여 원하는 작업을 수행할 수 있습니다.
             * 예: 마커 표시, 정보 업데이트 등
             */
            console.log('=== initPage 메시지를 받았습니다. === ');

            /** 부모 페이지에 메시지 수신 확인 응답 */
            const postSuccessData = {
              pageId,
              messageType
            };

            window.parent.postMessage(postSuccessData, origin);

            return;
          }

          /** getInfo */
          if (parentMode && messageType === 'getInfo') {
            if (parentMode === 'register') {
              /** 부모 페이지에 메시지 수신 확인 응답 */
              handleRiskAssessmentGetInfoType(origin);
            } else if (parentMode === 'beacon_register') {
              handleBeaconGetInfoType(origin);
            }
          }

          /** initProcessRate */
          if (messageType === 'initProcessRate') {
            console.log('=== initProcessRate 메시지를 받았습니다. === ', data);

            handleGetProcessRate(data);
          }
        }
      } catch (error) {
        console.error('Error parsing message:', error);
        openNotification({
          description: '메시지 처리 중 오류가 발생했습니다.',
          type: 'error'
        });
      }
    },
    [
      type,
      riskAssessmentList,
      openNotification,
      resetRecoilState,
      setParentData,
      setSiteInformation,
      setParentMetaData,
      setActiveTabKey,
      setPlayerData,
      setMarkerCreateMode,
      setUserInfo,
      parentMode,
      handleRiskAssessmentGetInfoType,
      handleBeaconGetInfoType
    ]
  );

  useEffect(() => {
    if (document.readyState === 'complete') {
      notifyParent();
    } else {
      window.addEventListener('load', notifyParent);
    }

    window.addEventListener('message', handleParentMessage);

    return () => {
      window.removeEventListener('load', notifyParent);
      window.removeEventListener('message', handleParentMessage);
    };
  }, [notifyParent, handleParentMessage]);

  useEffect(() => {
    if (type) setParentMode(type);
  }, [type, setParentMode]);

  return {
    parentData,
    parentMetaData
  };
};
